import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Translate } from '../../shared/LanguageService';
import FailureSnackbar from '../../shared/feedback/FailureSnackbar';
import SuccessSnackbar from '../../shared/feedback/SuccessSnackbar';
import { readApiErrorMessage } from './apiErrorMessage';
import FeatureFlagDialog from './FeatureFlagDialog';
import FeatureFlagsTable from './FeatureFlagsTable';
import { useDeleteFeatureFlagMutation } from './queries/useDeleteFeatureFlagMutation';
import { useFeatureFlagsQuery } from './queries/useFeatureFlagsQuery';
import { FeatureFlag } from './types';

export default function FeatureFlagsPage() {
    const flagsQuery = useFeatureFlagsQuery();
    const deleteMutation = useDeleteFeatureFlagMutation();

    const [editing, setEditing] = React.useState<FeatureFlag | null>(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [pendingDelete, setPendingDelete] = React.useState<FeatureFlag | null>(null);
    const [successText, setSuccessText] = React.useState<string | null>(null);
    const [errorText, setErrorText] = React.useState<string | null>(null);

    const openCreate = () => {
        setEditing(null);
        setDialogOpen(true);
    };

    const openEdit = (flag: FeatureFlag) => {
        setEditing(flag);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
    };

    const handleFlagSaved = (savedFlag: FeatureFlag, isCreate: boolean) => {
        setSuccessText(
            isCreate
                ? Translate('feature-flags/page/flag-created', { flagKey: savedFlag.key })
                : Translate('feature-flags/page/flag-updated', { flagKey: savedFlag.key })
        );
    };

    const requestDelete = (flag: FeatureFlag) => {
        setPendingDelete(flag);
    };

    const cancelDelete = () => {
        setPendingDelete(null);
    };

    const confirmDelete = async () => {
        if (pendingDelete === null) {
            return;
        }
        const flag = pendingDelete;
        setPendingDelete(null);
        try {
            await deleteMutation.mutateAsync(flag.key);
            setSuccessText(Translate('feature-flags/page/flag-deleted', { flagKey: flag.key }));
        } catch (error) {
            const errorMessage = await readApiErrorMessage(error);
            setErrorText(Translate('feature-flags/page/delete-error', { errorMessage }));
        }
    };

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
                <Typography variant="h4">{Translate('feature-flags/page/title')}</Typography>
                <Button variant="contained" onClick={openCreate}>
                    {Translate('feature-flags/page/new-flag-button')}
                </Button>
            </Stack>

            {flagsQuery.isLoading && (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            )}

            {flagsQuery.isError && (
                <Alert severity="error">{Translate('feature-flags/page/load-error')}</Alert>
            )}

            {flagsQuery.data && (
                <FeatureFlagsTable
                    flags={flagsQuery.data}
                    onEdit={openEdit}
                    onDelete={requestDelete}
                />
            )}

            <FeatureFlagDialog
                open={dialogOpen}
                onClose={closeDialog}
                onSaved={handleFlagSaved}
                flag={editing}
            />

            <Dialog
                open={pendingDelete !== null}
                onClose={cancelDelete}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>{Translate('feature-flags/page/delete-dialog/title')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {pendingDelete !== null
                            ? Translate('feature-flags/page/delete-dialog/confirm', {
                                  flagKey: pendingDelete.key
                              })
                            : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} disabled={deleteMutation.isLoading}>
                        {Translate('feature-flags/common/cancel')}
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteMutation.isLoading}
                    >
                        {deleteMutation.isLoading
                            ? Translate('feature-flags/page/delete-dialog/deleting')
                            : Translate('feature-flags/page/delete-dialog/delete-button')}
                    </Button>
                </DialogActions>
            </Dialog>

            <SuccessSnackbar
                open={successText !== null}
                onClose={() => setSuccessText(null)}
                text={successText != null ? successText : ''}
            />
            <FailureSnackbar
                open={errorText !== null}
                onClose={() => setErrorText(null)}
                text={errorText != null ? errorText : ''}
            />
        </Container>
    );
}
